/**
 * The renter's half of the database, which is two function calls and no library.
 *
 * WHY NOT THE SUPABASE CLIENT, WHEN THE WHOLE PRODUCT USES IT
 *
 * Two reasons, and the second one is a bug that would have been very hard to
 * find:
 *
 *   · The client exists to hold a SESSION — refresh it, persist it, read a
 *     credential out of a URL fragment. The renter has no session and must
 *     never acquire one. Every feature of that library is a liability on this
 *     page.
 *   · It keys its stored session by project URL. A second client constructed on
 *     the same project, in the same browser, writes the same storage key. An
 *     agent who opened a renter link in the tab next to the product could have
 *     had her own session stamped on by a page that is supposed to be
 *     anonymous.
 *
 * So this is `fetch`, one header set, and two POSTs. Everything it can reach is
 * decided by migration 0013: `anon` holds EXECUTE on exactly these two
 * functions and SELECT on nothing at all.
 *
 * NOTHING HERE READS THE CLOCK AND NOTHING HERE DECIDES ANYTHING. Whether a
 * quote has lapsed is the page's arithmetic on the frozen record; whether a
 * link has, and whether a home may be held, is the database's, because those
 * are the answers a renter's browser must not be able to influence.
 */

import { holdFromRow, priceLockFromRow } from './map.js'

/**
 * What the database says is promised to her, read into the product's own shapes.
 *
 * `lock` is what Confirm makes since migration 0022 (MTR #48). `hold` exists only after a lease is
 * signed — and, until 0022 is applied, it is also what the old `confirm_from_link` answers with,
 * so both are read: a page deployed a few minutes before its migration still tells the truth.
 */
export function readPromises(r) {
  return {
    lock: r?.lock ? priceLockFromRow(r.lock) : null,
    hold: r?.hold ? holdFromRow(r.hold) : null,
  }
}

const rpc = async (fn, args, { url, anonKey, fetchImpl }) => {
  const res = await (fetchImpl ?? fetch)(`${String(url).replace(/\/+$/, '')}/rest/v1/rpc/${fn}`, {
    method: 'POST',
    headers: {
      apikey: anonKey,
      authorization: `Bearer ${anonKey}`,
      'content-type': 'application/json',
      'content-profile': 'mtr',
    },
    body: JSON.stringify(args),
  })
  if (!res.ok) {
    /* A failure here is OURS, not hers, and the page says so in those words
       rather than printing a status code at somebody who cannot act on one. */
    const detail = await res.text().catch(() => '')
    const err = new Error(`the quote service answered ${res.status}`)
    err.detail = detail.slice(0, 400)
    throw err
  }
  return res.json()
}

export function createPublicQuoteApi({ url, anonKey, fetchImpl = null }) {
  if (!url || !anonKey) throw new Error('the renter page needs the project url and its anon key')
  const cfg = { url, anonKey, fetchImpl }
  return {
    /** `{ ok: true, quote, lock, hold }` or `{ ok: false, reason }` — never an exception for "no". */
    open: (token) => rpc('quote_for_link', { p_token: token }, cfg),

    /**
     * Lock her price on one home for 48 hours (MTR #48). It takes nothing off the market.
     *
     * It sends the figure that was on her screen, because the number she was
     * looking at when she pressed is the number any later argument is about.
     */
    confirm: ({ token, unitId, cleaningId = null, totalCents = null }) =>
      rpc('confirm_from_link', {
        p_token: token, p_unit_id: unitId,
        p_cleaning_id: cleaningId, p_total_cents: totalCents,
      }, cfg),
  }
}
